import { createClient } from "@supabase/supabase-js";

/**
 * Supabase client, used for authentication only.
 *
 * All application data goes through the Edge Function API rather than PostgREST: the
 * queries behind the study queue and the statistics page are far past what the REST
 * interface expresses, and keeping them server-side means the answer key never reaches
 * the browser before the learner has answered.
 *
 * Session storage note: supabase-js keeps the session in localStorage, which is readable
 * by any script on the page. The Fastify version kept the refresh token in an httpOnly
 * cookie instead. This is the standard Supabase SPA trade-off and is accepted here; the
 * mitigation is that the app ships no third-party scripts and has no user-generated HTML.
 */

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
    throw new Error(
        "VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY must be set at build time " +
            "(see apps/web/.env.example)"
    );
}

/**
 * The error the auth server put in the URL fragment, if any -- an expired or already-used
 * password-recovery link arrives as `#error=access_denied&error_description=...` and never
 * produces a session.
 *
 * Read here, at module load, because `detectSessionInUrl` clears the fragment as soon as the
 * client finishes initialising; a page component rendering later would find it empty.
 */
export const authUrlError: string | null = readAuthUrlError();

function readAuthUrlError(): string | null {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    return params.get("error_description") ?? params.get("error");
}

export const supabase = createClient(url, anonKey, {
    auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true
    }
});

/** Base URL of the Edge Function that serves the application API. */
export const API_BASE = `${url.replace(/\/$/, "")}/functions/v1/api`;

/** The anon key doubles as the gateway's API key header on every function call. */
export const SUPABASE_ANON_KEY = anonKey;
